import React, { useCallback, useEffect, useMemo } from 'react';
import i18n from 'i18next';
import { useCookies } from 'react-cookie';
import { createPortal } from 'react-dom';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { Outlet, useLoaderData, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { useGetUnitListByTypeQuery, useSearchReservationsQuery } from '@/api/rapidstor';
import { en } from '@/features/translations/en';
import { CorporationCode, getCorpTranslations } from '@/features/translations/helpers';
import { pt_pt } from '@/features/translations/pt-pt';
import { vi_vn } from '@/features/translations/vi-vn';
import { zh_hk } from '@/features/translations/zh-hk';
import Logger from '@/utils/logging';
import { skipToken } from '@reduxjs/toolkit/query';

import store from '@/store';
import { setContactData, setPersonaId, setReservationData, setUnitData } from '@/store/bookingSlice';

import Navbar from '../components/elements/navbar';
import DebugBar from '@/components/debug-bar';
import ContactCard from '@/components/elements/contact-card';

import LocationSettings from '../features/rapidstor/LocationSettings';
import { dispatchEvent } from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContextProvider } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContextProvider } from '@/features/rapidstor/providers/JourneyProvider';

export default function Root() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const [agentDetailsCookie] = useCookies(['agentDetails']);
    const routerLocation = useLocation();
    const [cookies, setCookie] = useCookies(['persona', 'selectedCustomerType', 'contactData', 'sessionId']);
    const [searchParams] = useSearchParams();

    const personaId = searchParams.get('personaId') || window.rapidstor_settings.rapidstor_options.persona || cookies.persona || undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deepMerge = useCallback((target: any, source: any) => {
        for (const key in source) {
            if (source[key] instanceof Object) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                deepMerge(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
        return target;
    }, []);

    useEffect(() => {
        Logger.info(`navigator.language: ${navigator.language}`);
    }, []);

    const resources = useMemo(() => ({
        en: deepMerge(en, getCorpTranslations(location.corpCode as CorporationCode)),
        'zh-HK': deepMerge(zh_hk, getCorpTranslations(location.corpCode as CorporationCode)),
        'pt-PT': deepMerge(pt_pt, getCorpTranslations(location.corpCode as CorporationCode)),
        'vi-VN': deepMerge(vi_vn, getCorpTranslations(location.corpCode as CorporationCode)),
    }), [location.corpCode, deepMerge]);

    useMemo(() => {
        i18n.use(initReactI18next).init({
            lng: window.rapidstor_settings.rapidstor_options.language || navigator.language,
            resources: resources,
            fallbackLng: 'en',
            interpolation: {
                escapeValue: false,
            },
        });
    }, [resources]);

    /**
     * Injects the site theme based on the location.
     */
    useEffect(() => {
        const personalisation = new LocationSettings(location);
        personalisation.injectSiteTheme();
    }, [location]);

    /**
     * Sets the personaId in the store if it exists
     */
    useEffect(() => {
        if (personaId) {
            setCookie(
                'persona',
                personaId,
                {
                    path: '/',
                    maxAge: 60 * 60 * 24
                }
            );
            store.dispatch(setPersonaId(personaId));
        }
    }, [personaId, setCookie]);

    // --- Reservation restore on startup ---
    const sessionId = cookies.sessionId;
    const { data: reservations, isSuccess } = useSearchReservationsQuery(
        sessionId && location ? {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            'externalData.SessionID': sessionId,
        } : skipToken
    );
    const {
        data: unitList,
        isFetching: unitListLoading,
    } = useGetUnitListByTypeQuery({
        locationCodes: [location.locationCode],
        persona: personaId,
        unitTypeNames: window.rapidstor_settings.rapidstor_options.unitTypeNames,
        unitTypeRegex: window.rapidstor_settings.rapidstor_options.unitTypeNames ? undefined : window.rapidstor_settings.rapidstor_options.unitTypeRegex,
    });

    useEffect(() => {
        if (isSuccess && reservations.length && !unitListLoading && unitList) {
            const reservation = reservations[reservations.length - 1];

            const unitType = unitList.unitTypes.find((ut) => ut._id === reservation.unitType._id);
            const descriptor = unitList.descriptors.find((d) => d._id === reservation.unitType.descriptor._id);

            if (unitType && descriptor) {
                const unitToUse = unitType?.units.find((u) => u._id === reservation.unitType.unitToUse._id);
                const dealObject = unitType?.deals.find((d) => d._id === reservation.deal?._id);
                store.dispatch(setReservationData({
                    unitType,
                    unitToUse,
                    descriptor,
                    deal: reservation.deal?._id,
                    dealObject,
                    contactData: {
                        neededDate: String(reservation.moveInContext.neededDate),
                        firstName: reservation.firstName,
                        lastName: reservation.lastName,
                        email: reservation.email,
                        phone: reservation.phone,
                    },
                    token: reservation.token,
                }));

                store.dispatch(setUnitData({
                    location,
                    unitType,
                    descriptors: unitList.descriptors,
                    descriptor,
                }));
            }

            store.dispatch(setContactData({
                firstName: reservation.firstName,
                lastName: reservation.lastName,
                phone: reservation.phone,
                email: reservation.email,
                neededDate: String(reservation.moveInContext.neededDate),
            }));

            if (routerLocation.pathname === '/') {
                navigate('/customise-booking');
            }
        }
        // We exclude routerLocation from the dependency array to avoid infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, reservations, location, navigate, unitListLoading, unitList]);
    // --- End reservation restore ---

    /**
     * Dispatches the location changed event to the parent window
     */
    useEffect(() => {
        dispatchEvent({
            type: 'location-changed',
            context: {
                pathname: routerLocation.pathname,
                hash: routerLocation.hash,
            },
        });
    }, [routerLocation]);

    /**
     * Handles the agent details cookies for the UKStorage Callcenter Dashboard.
     * Pushes data through into the externalData object in the reservation payloads.
     */
    useEffect(() => {
        if (agentDetailsCookie && agentDetailsCookie.agentDetails) {
            window.rapidstor_settings.app_config.journey.agentDetails = {
                agentName: agentDetailsCookie.agentDetails.agentName,
                agentComment: agentDetailsCookie.agentDetails.comments,
            };
        }
    }, [agentDetailsCookie]);

    return (
        <>
            <AnalyticsContextProvider location={location}>
                <I18nextProvider i18n={i18n}>
                    <JourneyContextProvider location={location}>
                        <>
                            <Navbar location={location} />

                            <Outlet />

                            {createPortal(<ContactCard location={location} />, document.body)}
                        </>
                    </JourneyContextProvider>
                </I18nextProvider>
            </AnalyticsContextProvider>
            {window.rapidstor_settings.app_config.journey.configSelector && <DebugBar />}
        </>
    );
}
